
import React, { useState, useMemo, useEffect } from 'react';
import { USERS } from '../constants';
import { EducationEvent, Article } from '../types';
import { dbService } from '../lib/firebase';
import { BookOpen, Upload, FileText, Calendar, Download, Plus, Clock, X, Edit2, Trash2, Save, Users, CheckCircle2, Video, Loader2 } from 'lucide-react';

export const Education: React.FC = () => {
    const [events, setEvents] = useState<EducationEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const [downloadingArticleId, setDownloadingArticleId] = useState<string | null>(null);
    
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadTargetResidentId, setUploadTargetResidentId] = useState<string | null>(null);
    const [uploadForm, setUploadForm] = useState({ title: '', file: null as File | null });

    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [editingEventId, setEditingEventId] = useState<string | null>(null);
    const [eventForm, setEventForm] = useState({
        title: '',
        date: '',
        time: '07:00 AM',
        type: 'Journal Club' as 'Journal Club' | 'Morbidity & Mortality' | 'Topic Rounds',
        meetingLink: '',
    });

    const TEAMS_LINK = "https://nam12.safelinks.protection.outlook.com/ap/t-59584e83/?url=https%3A%2F%2Fteams.microsoft.com%2Fl%2Fmeetup-join%2F19%253ameeting_ZjI0MmVhYWEtYTQzNy00OTIyLTlhZTctN2JiZmFhMzI2MjQ2%2540thread.v2%2F0%3Fcontext%3D%257b%2522Tid%2522%253a%252229ecdc93-e9fc-4cf4-9d3f-b4af8d2e140c%2522%252c%2522Oid%2522%253a%2522bd7f8741-fecc-4e7e-a04b-dcd27323ca2a%2522%257d&data=05%7C02%7CConnor.Pyne%40nva.com%7Cd0b4a893914b4866927908dcbc589e88%7C29ecdc93e9fc4cf49d3fb4af8d2e140c%7C0%7C0%7C638592336042889360%7CUnknown%7CTWFpbGZsb3d8eyJWIjoiMC4wLjAwMDAiLCJQIjoiV2luMzIiLCJBTiI6Ik1haWwiLCJXVCI6Mn0%3D%7C0%7C%7C%7C&sdata=KslJJj874rcsPcKACXIJqlORom6eWfvEkx8tjSrXznw%3D&reserved=0";

    const residents = useMemo(() => USERS.filter(u => u.role === 'Resident'), []);

    useEffect(() => {
        const unsubscribe = dbService.subscribeToEducationEvents((data) => {
            setEvents(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const selectedEvent = useMemo(() => {
        if (!selectedEventId) return events.length > 0 ? events[0] : null;
        return events.find(e => e.id === selectedEventId) || null;
    }, [events, selectedEventId]);

    const handleUploadClick = (residentId: string | null) => {
        setUploadTargetResidentId(residentId);
        setUploadForm({ title: '', file: null });
        setIsUploadModalOpen(true);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setUploadForm({ ...uploadForm, file: e.target.files[0] });
        }
    };

    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleSaveUpload = async () => {
        if (!selectedEvent || !uploadForm.title || !uploadForm.file) return;

        setIsUploading(true);

        try {
            const articleId = `art-${Date.now()}`;
            let base64Url = "";

            if (dbService.isConnected()) {
                await dbService.uploadArticleFile(articleId, uploadForm.file);
            } else {
                base64Url = await convertFileToBase64(uploadForm.file);
            }
            
            const newArticle: Article = {
                id: articleId,
                title: uploadForm.title,
                fileName: uploadForm.file.name,
                uploadDate: new Date().toISOString().split('T')[0],
                size: `${(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB`,
                assignedResidentId: uploadTargetResidentId || undefined,
                url: base64Url 
            };

            const updatedEvent = { 
                ...selectedEvent, 
                articles: [...selectedEvent.articles, newArticle] 
            };

            setIsUploadModalOpen(false);
            await dbService.saveEducationEvent(updatedEvent);
        } catch (error) {
            console.error("Upload failed", error);
            alert("Failed to process file.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownload = async (e: React.MouseEvent, article: Article) => {
        e.preventDefault();
        
        if (article.url && article.url.startsWith('data:')) {
            const a = document.createElement('a');
            a.href = article.url;
            a.download = article.fileName;
            a.click();
            return;
        }

        setDownloadingArticleId(article.id);
        try {
            const base64Data = await dbService.downloadArticleFile(article.id);
            if (base64Data) {
                const a = document.createElement('a');
                a.href = base64Data;
                a.download = article.fileName;
                a.click();
            } else {
                alert("File not found in database.");
            }
        } catch (error) {
            console.error(error);
            alert("Error downloading file.");
        } finally {
            setDownloadingArticleId(null);
        }
    };

    const handleDeleteArticle = async (e: React.MouseEvent, articleId: string) => {
        e.stopPropagation();
        if (!selectedEvent) return;
        
        const updatedEvent = { 
            ...selectedEvent, 
            articles: selectedEvent.articles.filter(a => a.id !== articleId) 
        };

        await dbService.saveEducationEvent(updatedEvent);
        
        if (dbService.isConnected()) {
            await dbService.deleteArticleFile(articleId);
        }
    };

    const handleAddEvent = () => {
        setEditingEventId(null);
        setEventForm({
            title: '',
            date: new Date().toISOString().split('T')[0],
            time: '07:00 AM',
            type: 'Journal Club',
            meetingLink: '',
        });
        setIsEventModalOpen(true);
    };

    const handleEditEvent = (e: React.MouseEvent, evt: EducationEvent) => {
        e.stopPropagation(); 
        e.preventDefault();
        setEditingEventId(evt.id);
        setEventForm({
            title: evt.title,
            date: evt.date,
            time: evt.time,
            type: evt.type,
            meetingLink: evt.meetingLink || '',
        });
        setIsEventModalOpen(true);
    };

    const handleDeleteEvent = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        e.preventDefault();
        if (selectedEventId === id) {
            setSelectedEventId(null);
        }
        await dbService.deleteEducationEvent(id);
    };

    const handleSaveEvent = async () => {
        if (!eventForm.title || !eventForm.date) return;

        const presenterDisplay = "Surgical Residents";

        const newEvent: EducationEvent = {
            id: editingEventId || `evt-${Date.now()}`,
            title: eventForm.title,
            date: eventForm.date,
            time: eventForm.time,
            type: eventForm.type,
            presenter: presenterDisplay,
            meetingLink: eventForm.meetingLink,
            articles: editingEventId ? (events.find(e => e.id === editingEventId)?.articles || []) : []
        };

        setIsEventModalOpen(false);
        await dbService.saveEducationEvent(newEvent);
    };

    const renderArticleItem = (art: Article) => (
        <div key={art.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm group hover:shadow-sm transition-all mb-2">
            <div className="flex items-center gap-3 overflow-hidden">
                <div className="bg-red-50 dark:bg-red-900/30 p-1.5 rounded text-red-600 dark:text-red-400 flex-shrink-0">
                    <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">{art.title}</div>
                    <div className="text-xs text-slate-400 truncate">{art.fileName} • {art.size}</div>
                </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <button 
                    onClick={(e) => handleDownload(e, art)}
                    className="p-1.5 text-slate-400 hover:text-blue-900 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors flex items-center justify-center cursor-pointer disabled:opacity-50"
                    title="Download"
                    disabled={downloadingArticleId === art.id}
                >
                    {downloadingArticleId === art.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
                <button 
                    onClick={(e) => handleDeleteArticle(e, art.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors" 
                    title="Remove"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col p-6 overflow-hidden relative">
            <header className="mb-6 flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-3 brand-font">
                        <BookOpen className="w-8 h-8 text-blue-900 dark:text-blue-400" />
                        Educational Events
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">Weekly Rounds & Resident Journal Club</p>
                </div>
                <div className="flex gap-4 items-center">
                    <div className="hidden lg:block bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg border border-blue-100 dark:border-blue-900/50">
                        <div className="flex items-center gap-2 text-blue-900 dark:text-blue-300 font-bold text-sm uppercase">
                            <Clock className="w-4 h-4" />
                            Rounds: Wednesdays 7:00 AM
                        </div>
                    </div>
                    
                    <a 
                        href={TEAMS_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-sm text-sm"
                    >
                        <Video className="w-5 h-5" /> General Rounds
                    </a>

                    <button 
                        onClick={handleAddEvent}
                        className="bg-blue-900 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-sm"
                    >
                        <Plus className="w-5 h-5" /> Add Event
                    </button>
                </div>
            </header>

            <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden">
                <div className="w-full lg:w-1/3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                        <h3 className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 font-serif">
                            <Calendar className="w-4 h-4" /> Schedule
                        </h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {loading ? (
                             <div className="p-8 flex justify-center">
                                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                            </div>
                        ) : events.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 font-medium">No events scheduled.</div>
                        ) : (
                            events.map(evt => (
                                <div
                                    key={evt.id}
                                    onClick={() => setSelectedEventId(evt.id)}
                                    className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer group relative ${
                                        (selectedEvent?.id) === evt.id 
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-900 dark:border-blue-500 ring-1 ring-blue-900 dark:ring-blue-500' 
                                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-1 pr-16">
                                        <div className="font-bold text-slate-800 dark:text-slate-200 truncate font-serif">{evt.title}</div>
                                    </div>
                                    <div className="flex justify-between items-center text-sm text-slate-500 dark:text-slate-400 mb-1">
                                        <span className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded text-xs font-bold">{evt.date}</span>
                                        <span className="text-blue-900 dark:text-blue-400 font-bold">{evt.time}</span>
                                    </div>
                                    <div className="text-xs text-slate-400 uppercase tracking-wide font-bold">{evt.type}</div>

                                    <div className="absolute top-3 right-3 flex gap-1 z-20">
                                        <button 
                                            onClick={(e) => handleEditEvent(e, evt)}
                                            className="p-1.5 bg-white dark:bg-slate-900 text-slate-400 hover:text-blue-900 dark:hover:text-blue-400 rounded border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition-all opacity-0 group-hover:opacity-100"
                                            title="Edit Event"
                                        >
                                            <Edit2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button 
                                            onClick={(e) => handleDeleteEvent(e, evt.id)}
                                            className="p-1.5 bg-white dark:bg-slate-900 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition-all opacity-0 group-hover:opacity-100"
                                            title="Delete Event"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="w-full lg:w-2/3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden">
                    {selectedEvent ? (
                        <>
                            <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-3 mb-3">
                                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-bold uppercase tracking-wide">{selectedEvent.type}</span>
                                            <span className="text-slate-500 dark:text-slate-400 text-sm font-bold flex items-center gap-1 bg-white dark:bg-slate-900 px-2 py-1 rounded border border-slate-200 dark:border-slate-600">
                                                <Calendar className="w-3.5 h-3.5" /> {selectedEvent.date}
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 text-sm font-bold flex items-center gap-1 bg-white dark:bg-slate-900 px-2 py-1 rounded border border-slate-200 dark:border-slate-600">
                                                <Clock className="w-3.5 h-3.5" /> {selectedEvent.time}
                                            </span>
                                        </div>
                                        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2 brand-font">{selectedEvent.title}</h2>
                                        
                                        {selectedEvent.meetingLink && (
                                            <a 
                                                href={selectedEvent.meetingLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 mt-1 mb-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-bold rounded-lg border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                            >
                                                <Video className="w-4 h-4" /> Join Virtual Meeting
                                            </a>
                                        )}
                                        
                                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">Upload and review assigned journal articles below.</p>
                                    </div>
                                    <button 
                                        onClick={() => handleUploadClick(null)}
                                        className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-blue-900 dark:hover:border-blue-400 text-slate-600 dark:text-slate-300 px-4 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-sm text-sm whitespace-nowrap"
                                    >
                                        <Upload className="w-4 h-4" /> Upload General
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 bg-slate-100 dark:bg-slate-900/50">
                                <div className="space-y-6">
                                    {residents.map(resident => {
                                        const residentArticles = selectedEvent.articles.filter(a => a.assignedResidentId === resident.id);
                                        const hasArticles = residentArticles.length > 0;

                                        return (
                                            <div key={resident.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                                <div className="p-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300 flex items-center justify-center font-bold font-serif border border-blue-200 dark:border-blue-800">
                                                            {resident.lastName.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-slate-800 dark:text-slate-200 font-serif">Dr. {resident.lastName}</div>
                                                            <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-bold">Resident</div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        {hasArticles ? (
                                                            <span className="text-xs font-bold text-green-700 dark:text-green-400 flex items-center gap-1 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full border border-green-200 dark:border-green-800">
                                                                <CheckCircle2 className="w-3 h-3" /> Ready
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full border border-slate-200 dark:border-slate-600">
                                                                Pending
                                                            </span>
                                                        )}
                                                        <button 
                                                            onClick={() => handleUploadClick(resident.id)}
                                                            className="p-2 text-blue-900 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
                                                            title="Upload Article for Resident"
                                                        >
                                                            <Upload className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="p-4 min-h-[60px]">
                                                    {hasArticles ? (
                                                        residentArticles.map(renderArticleItem)
                                                    ) : (
                                                        <div className="text-xs text-slate-400 italic pl-2 font-medium">No articles assigned.</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center border border-slate-300 dark:border-slate-600">
                                                    <Users className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-800 dark:text-slate-200 font-serif">General / Shared</div>
                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-bold">Resources</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            {selectedEvent.articles.filter(a => !a.assignedResidentId).length > 0 ? (
                                                selectedEvent.articles.filter(a => !a.assignedResidentId).map(renderArticleItem)
                                            ) : (
                                                <div className="text-xs text-slate-400 italic pl-2 font-medium">No general documents.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50 dark:bg-slate-900/30">
                            <BookOpen className="w-16 h-16 opacity-10 mb-4" />
                            <p className="font-medium">Select an event from the schedule to manage assignments.</p>
                        </div>
                    )}
                </div>
            </div>

            {isUploadModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-700">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                            <h3 className="font-bold text-slate-800 dark:text-white brand-font">
                                Upload Article 
                                {uploadTargetResidentId && <span className="text-blue-900 dark:text-blue-300 ml-2 font-normal text-sm font-sans">for Dr. {USERS.find(u => u.id === uploadTargetResidentId)?.lastName}</span>}
                            </h3>
                            <button onClick={() => setIsUploadModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Article Title</label>
                                <input 
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white" 
                                    placeholder="e.g. Effects of..."
                                    value={uploadForm.title}
                                    onChange={e => setUploadForm({...uploadForm, title: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">PDF File</label>
                                <input 
                                    type="file" 
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="w-full text-sm text-slate-900 dark:text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-900 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50 cursor-pointer"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Files are chunked and synced. Large files may take longer.</p>
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
                            <button onClick={() => setIsUploadModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
                            <button 
                                onClick={handleSaveUpload} 
                                disabled={!uploadForm.file || !uploadForm.title || isUploading} 
                                className="px-4 py-2 bg-blue-900 dark:bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-800 dark:hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {isUploading ? "Uploading..." : "Upload"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isEventModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-700">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                            <h3 className="font-bold text-slate-800 dark:text-white brand-font">{editingEventId ? 'Edit Event' : 'Add New Event'}</h3>
                            <button onClick={() => setIsEventModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Event Title</label>
                                <input 
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white" 
                                    placeholder="e.g. Journal Club: Orthopedics"
                                    value={eventForm.title}
                                    onChange={e => setEventForm({...eventForm, title: e.target.value})}
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Date</label>
                                    <input 
                                        type="date"
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white" 
                                        value={eventForm.date}
                                        onChange={e => setEventForm({...eventForm, date: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Time</label>
                                    <input 
                                        type="time"
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white" 
                                        value={eventForm.time}
                                        onChange={e => setEventForm({...eventForm, time: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Type</label>
                                <select 
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                    value={eventForm.type}
                                    onChange={e => setEventForm({...eventForm, type: e.target.value as any})}
                                >
                                    <option>Journal Club</option>
                                    <option>Morbidity & Mortality</option>
                                    <option>Topic Rounds</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Virtual Meeting Link (Optional)</label>
                                <div className="relative">
                                    <input 
                                        className="w-full pl-9 p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white" 
                                        placeholder="Paste Teams/Zoom link here..."
                                        value={eventForm.meetingLink}
                                        onChange={e => setEventForm({...eventForm, meetingLink: e.target.value})}
                                    />
                                    <Video className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
                            <button onClick={() => setIsEventModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
                            <button onClick={handleSaveEvent} className="px-4 py-2 bg-blue-900 dark:bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-800 dark:hover:bg-blue-500 flex items-center gap-2">
                                <Save className="w-4 h-4" /> Save Event
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
